import React, { Component } from "react";
import PropTypes from "prop-types";
import { graphql } from "react-apollo";
import { observable } from "mobx";
import { observer } from "mobx-react";
import moment from "moment";
import { queryGetStreamsPagination } from "Apollo";
import { CommonStore, GetSubsStreams, CaseManagementStore, ShowNotice } from "Store";
import LoadingPanel from "module-common/LoadingPanel";
import MessageListView from "./MessageListView";

// this component will fetch and prepare message data to be rendered
export default graphql(queryGetStreamsPagination)(
  observer(
    class MessageList extends Component {
      static propTypes = { store: PropTypes.object.isRequired };
      states = observable({ loadingPagination: false });

      // reset states on case id change
      componentWillReceiveProps(nextProps) {
        if (this.props.case.id !== nextProps.case.id) {
          this.states.loadingPagination = false;
        }
      }

      // release scrollbar ref when unmounting
      componentWillUnmount() {
        const store = this.props.store;
        store.messageScrollBarRef = null;
      }

      createOriginalPostLink(message) {
        const channelName = message.channel.name
        const specificTypeName = message.specificTypeId.name
        let link = null
        if(channelName == 'twitter') {
          if(specificTypeName == 'status') {
            link = "https://twitter.com/" +  message.channelHandler.displayName + "/status/" + message.originalId  
          }
        } else if(channelName == 'facebook') {
          if(specificTypeName != 'message') {
            link = "https://facebook.com/" + message.originalId  
          }
        } else if(channelName == 'instagram') {
          if(message.companyContextStream) {
            if(message.companyContextStream.secondaryOriginalId) {
              link = "https://instagram.com/p/" + message.companyContextStream.secondaryOriginalId   
            }
          }          
        } else link = null
        return link
      }

      render() {
        const {
          store,
          data: { loading, getStreamsPagination: streamsConnection }
        } = this.props;
        const selected = store.selectedCase;

        // if loading, show loading panel
        if (loading) return <LoadingPanel flex={2} />;

        // combine streams from query and subs
        let combined = {};
        if (streamsConnection) {
          for (let edge of streamsConnection.edges) {
            const node = edge.node;
            combined[node.id] = node;
          }
        }
        if(selected) Object.assign(combined, GetSubsStreams(selected.id));

        // prepare messages data
        const messages = [];
        for (const key in combined) {
          const message = combined[key];
          const customer = selected.customerChannelHandler;
          let fromAgent = message.createdBy !== null;
          if (message.fromId === message.channelHandler.originalId) {
            if (fromAgent !== null) {
              fromAgent = true;
            }
          } else {
            fromAgent = false;
          }
          const agent = message.createdBy !== null ? message.createdBy : null;

          const avatar =
            message.createdBy !== null
              ? agent.displayPicture
              : customer.displayPicture;
          const noNeed =
            message.labels &&
            message.labels.find(
              label => label.labelTemplate.id === CommonStore.noNeedId
            );
          const reply =
            message.replyMode === "EXPLICIT" ? message.replyToStream : null;
          const replyName =
            reply && reply.createdBy
              ? reply.createdBy.displayName
              : customer.name;
          const replyContent = reply ? reply.content : null;
          let imageUrl = null
          if(message.media && (message.media.type == 'image' || message.media.type == 'video')) {
            imageUrl = message.media.url
          }
          let fileUrl = null
          if(message.media && message.media.type == 'file') {
            fileUrl = message.media.url
          }

          // Company Context
          const { selectedCase } = CaseManagementStore;
          const channelContext =
            selectedCase !== null
              ? selectedCase.channel.name === "instagram" &&
                message.companyContextStream
              : "";
          const imageContext = channelContext
            ? (message.companyContextStream.media ? message.companyContextStream.media.url : null)
            : null;
          const channelHandlerCompany = channelContext
            ? message.companyContextStream.channelHandler.name
            : null;
          const contentCompany = channelContext
            ? message.companyContextStream.content
            : null;
          const sentByName = message.createdBy ? message.createdBy.displayName : null
          // Company Context

          const channelTypeId = message.specificTypeId.id;
          const channelType = message.specificTypeId.name;
          const replyImage = reply ? message.media : null;
          const channelName = message.channel.name;
          const originalPost = this.createOriginalPostLink(message) 
          messages.push({
            id: message.id,
            content: message.content,
            fromAgent: fromAgent,
            replyName: replyName,
            sentByName: sentByName,
            replyContent: replyContent,
            replyImage: replyImage,
            showOption: !fromAgent,
            showOptionMenu: selected.assignedTo !== null,
            time: new moment(message.dateCreated),
            labels: message.labels,
            avatar: avatar,
            noNeed: noNeed !== undefined,
            imageUrl: imageUrl,
            channelType: channelType,
            imageContext,
            channelHandlerCompany,
            contentCompany,
            timeCompany: new moment(
              channelContext ? message.companyContextStream.dateCreated : null
            ),
            channelName: channelName,
            originalPost: originalPost,
            isOldCase: message.isOldCase,
            channelTypeId: channelTypeId,
            fileUrl: fileUrl
          });
        }

        // sort messages by time
        messages.sort((a, b) => b.time - a.time);

        // send to view component to be rendered
        return (
          <MessageListView
            id={selected.id}
            messages={messages}
            scrollRef={ref => (store.messageScrollBarRef = ref)}
            onEndReached={this.onEndReached}
          />
        );
      }

      // trigger data fetching if we reached top most message
      onEndReached = async () => {
        const {
          data: { fetchMore, getStreamsPagination: streamsConnection }
        } = this.props;

        // check if we have previous data
        if (streamsConnection && streamsConnection.edges.length > 0) {
          // prevent multiple fetching to avoid data duplication
          if (!this.states.loadingPagination) {
            this.states.loadingPagination = true;

            // get top most stream
            const length = streamsConnection.edges.length;
            const cursor = streamsConnection.edges[length - 1].cursor;

            // fetch more data
            try {
              await fetchMore({
                variables: {
                  after: cursor
                },
                updateQuery: (prevResult, { fetchMoreResult }) => {
                  if (!fetchMoreResult) return prevResult;
                  const result = Object.assign(
                    {},
                    prevResult.getStreamsPagination,
                    {
                      edges: [
                        ...prevResult.getStreamsPagination.edges,
                        ...fetchMoreResult.getStreamsPagination.edges
                      ]
                    }
                  );
                  return { getStreamsPagination: result };
                }
              });
              this.states.loadingPagination = false;    
            } catch(e) {
              ShowNotice("server-error-retry-later")
              this.states.loadingPagination = false;
            }
            
          }
        }
      };
    }
  )
);



// WEBPACK FOOTER //
// ./src/module-case-management/content-panel/message-list/MessageListWithStoreQuery.js